import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { io } from "socket.io-client";
import { PublicKey, Transaction } from "@solana/web3.js";
import { useProgram } from "../App";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";

import CustomReact95Toast from "../components/CustomReact95Toast";
import GlobalJackpotWinnerPopup from "../components/GlobalJackpotWinnerPopup";
import { Buffer } from "buffer";
window.Buffer = window.Buffer || Buffer; // make it global for every lib

export const NotificationContext = createContext();

const NotificationProvider = ({ children, walletAddress }) => {
  /* ─────────────────────────────── state ────────────────────────────── */
  const [notifications, setNotifications] = useState([]);
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true);

  const [jackpot, setJackpot] = useState(0);
  const [totalTickets, setTotalTickets] = useState(0);
  const [participants, setParticipants] = useState(0);

  const [globalTickets, setGlobalTickets] = useState([]); // the user’s ticket #s
  const [winnerTicket, setWinnerTicket] = useState(null); // raw number from the program

  const [showWinnerPopup, setShowWinnerPopup] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [jackpotClaimed, setJackpotClaimed] = useState(false);
  const [seasonId, setSeasonId] = useState(1);

  // Track when the global jackpot concludes so winner polling only
  // begins after the jackpot is closed
  const [jackpotEndTime, setJackpotEndTime] = useState(null);
  const [jackpotClosed, setJackpotClosed] = useState(false);

  const [notificationCounters, setNotificationCounters] = useState({
    profile: 0,
    tickets: 0,
    competitions: 0,
    winnings: 0,
    jackpot: 0,
  });

  const socketRef = useRef(null);

  const getCategory = (n) => {
    const title = n.title || "";
    if (title.includes("Entry")) return "tickets";
    if (title.includes("Competition")) return "competitions";
    if (title.includes("Global Jackpot")) return "jackpot";
    if (title.includes("Prize Claimed")) return "winnings";
    if (title.includes("Top_Off") || title.includes("Purchase"))
      return "profile";
    return "profile";
  };

  // Push a notification to state and show the toast
  const addNotification = useCallback((n) => {
    setNotifications((prev) => [n, ...prev]);
    const category = getCategory(n);
    setNotificationCounters((prev) => ({
      ...prev,
      [category]: prev[category] + 1,
    }));
    toast(
      (tp) => (
        <CustomReact95Toast
          title={n.title || n.type || "Notification"}
          message={n.message}
          closeToast={tp.closeToast}
        />
      ),
      {
        position: "top-right",
        autoClose: 5000,
        closeButton: false,
        hideProgressBar: false,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
      }
    );
  }, []);

  const clearNotificationCount = useCallback((category) => {
    setNotificationCounters((prev) => ({ ...prev, [category]: 0 }));
  }, []);

  /* anchors / wallet hooks for the on-chain claim */
  const { program } = useProgram();
  const { publicKey, wallet } = useWallet();
  const { connection } = useConnection();

  /* ─────────────────────── fetch jackpot config ───────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/public-global-jackpot-config`
        );
        if (data?.end_time) {
          const endMs = parseInt(data.end_time, 10) * 1000;
          setJackpotEndTime(endMs);
          if (Date.now() >= endMs) setJackpotClosed(true);
        }
      } catch (e) {
        console.error("Error fetching jackpot config:", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!jackpotEndTime) return;
    if (Date.now() >= jackpotEndTime) {
      setJackpotClosed(true);
      return;
    }
    const id = setTimeout(
      () => setJackpotClosed(true),
      jackpotEndTime - Date.now()
    );
    return () => clearTimeout(id);
  }, [jackpotEndTime]);

  /* ───────────────────────── poll once per minute ──────────────────── */
  useEffect(() => {
    if (!walletAddress || !jackpotClosed) return;

    const pollWinner = async () => {
      try {
        const { data, status } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/global-jackpot/winner`,
          { params: { ts: Date.now() }, validateStatus: () => true }
        );
        if (status === 304) return; // not changed

        const win = data.winning_ticket ?? data.winningTicket;
        if (data?.status === "completed" && win) {
          setWinnerTicket(win);
          if (!jackpotClaimed) setShowWinnerPopup(true);
        }
      } catch (e) {
        console.error("pollWinner error:", e);
      }
    };

    pollWinner();
    const id = setInterval(pollWinner, 60_000);
    return () => clearInterval(id);
  }, [walletAddress, jackpotClaimed]);

  /* ─────────────────────── on-chain claim handler ──────────────────── */
  const handleClaim = async () => {
    if (!program || !wallet || !publicKey || !winnerTicket) return;
    setClaiming(true);

    try {
      /* 1. derive PDAs ------------------------------------------------- */
      const [globalJackpotPda] = await PublicKey.findProgramAddress(
        [Buffer.from("global_jackpot")],
        program.programId
      );
      console.log("🪙  globalJackpot PDA  :", globalJackpotPda.toBase58());

      const [ticketPda] = await PublicKey.findProgramAddress(
        [Buffer.from("global_jackpot_ticket"), publicKey.toBuffer()],
        program.programId
      );
      console.log("🎫 globalJackpotTicket PDA (winner):", ticketPda.toBase58());

      /* 2. build instruction ------------------------------------------ */
      const ix = await program.methods
        .claimGlobalJackpotPrize()
        .accounts({
          globalJackpot: globalJackpotPda,
          winner: publicKey, // signer
          globalJackpotTicket: ticketPda,
        })
        .instruction();

      /* 3. sign & send ------------------------------------------------- */
      const tx = new Transaction().add(ix);
      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash();
      tx.recentBlockhash = blockhash;
      tx.feePayer = publicKey;

      const signed = await wallet.adapter.signTransaction(tx);
      const signature = await connection.sendRawTransaction(signed.serialize());
      console.log("✉️  tx signature:", signature);

      await connection.confirmTransaction(
        { signature, blockhash, lastValidBlockHeight },
        "finalized"
      );

      const parsed = await connection.getParsedTransaction(signature, {
        maxSupportedTransactionVersion: 0,
      });
      if (parsed?.meta?.err) {
        console.error("Program log:", parsed.meta.logMessages);
        throw new Error(
          "claim failed on-chain: " + JSON.stringify(parsed.meta.err)
        );
      }

      /* 4. (optional) notify backend so it can log the claim ---------- */
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/global-jackpot/claim`,
          { user_id: publicKey.toBase58(), txSignature: signature, blockhash }
        );
        if (typeof data.claimed === "boolean") {
          setJackpotClaimed(data.claimed);
        } else {
          // Fallback to avoid the popup reappearing due to missing flag
          setJackpotClaimed(true);
        }
      } catch (e) {
        // Even if backend logging fails, mark as claimed locally so the
        // winner popup doesn't reopen on the next poll
        setJackpotClaimed(true);
        console.warn("[backend-log] global-jackpot claim:", e.message);
      }

      toast(
        (tp) => (
          <CustomReact95Toast
            title="Success"
            message="Global-jackpot prize claimed! 🎉"
            closeToast={tp.closeToast}
          />
        ),
        {
          position: "top-right",
          autoClose: 5000,
          closeButton: false,
          hideProgressBar: false,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
        }
      );
      setShowWinnerPopup(false);
    } catch (err) {
      console.error("claimGlobalJackpot error:", err);
      toast(
        (tp) => (
          <CustomReact95Toast
            title="Error"
            message={err.message ?? "Claim failed – see console"}
            closeToast={tp.closeToast}
          />
        ),
        {
          position: "top-right",
          autoClose: 5000,
          closeButton: false,
          hideProgressBar: false,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
        }
      );
    } finally {
      setClaiming(false);
    }
  };

  /* ─────────────────────── initial public stats ───────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/global-jackpot/stats`
        );
        setJackpot(data.totalJackpot);
        setTotalTickets(data.totalTickets);
        setParticipants(data.totalParticipants);
        if (typeof data.seasonId === "number") setSeasonId(data.seasonId);
      } catch (e) {
        console.error("Error fetching jackpot stats:", e);
      }
    })();
  }, [walletAddress]);

  /* ───────────── fetch the user’s ticket list (once per login) ─────── */
  useEffect(() => {
    if (!walletAddress) return;
    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/${walletAddress}/global-jackpot`
      )
      .then(({ data }) => setGlobalTickets(data.ticket_numbers || []))
      .catch(() => {});
  }, [walletAddress]);

  /* ───────────────────── single socket lifecycle ───────────────────── */
  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket = io(process.env.REACT_APP_API_BASE_URL);
    if (walletAddress) socket.emit("identify", walletAddress);

    /* push notifications --------------------------------------------- */
    socket.on("notification", async (n) => {
      addNotification(n);
      try {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-as-read`,
          { notificationIds: [n.id] }
        );
        setNotifications((p) => p.filter((x) => x.id !== n.id));
      } catch (e) {
        console.error("mark-as-read error:", e);
      }
    });

    socket.on("globalJackpotWinner", ({ winningTicket }) => {
      setWinnerTicket(winningTicket);
      if (!jackpotClaimed) setShowWinnerPopup(true);
    });

    /* live jackpot counters ------------------------------------------ */
    socket.on(
      "globalJackpotUpdate",
      ({ totalJackpot, totalTickets, participants }) => {
        if (!liveUpdatesEnabled) return;
        setJackpot(totalJackpot);
        setTotalTickets(totalTickets);
        setParticipants(participants);

        if (walletAddress) {
          axios
            .get(
              `${process.env.REACT_APP_API_BASE_URL}/api/users/${walletAddress}/global-jackpot`
            )
            .then(({ data }) => setGlobalTickets(data.ticket_numbers || []))
            .catch(() => {});
        }
      }
    );

    // socket.on("connect", () => console.log("[socket] connected:", socket.id));
    // socket.on("disconnect", () => console.log("[socket] disconnected"));

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [walletAddress, liveUpdatesEnabled]);

  /* ─────────────────────── unread notifications ───────────────────── */
  useEffect(() => {
    if (!walletAddress) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/notifications/${walletAddress}`
        );
        data.forEach((n) => addNotification(n));
        if (data.length) {
          await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-as-read`,
            { notificationIds: data.map((n) => n.id) }
          );
        }
      } catch (e) {
        console.error("unread notifications error:", e);
      }
    })();
  }, [walletAddress]);

  /* ───────────────────────── provider payload ─────────────────────── */
  return (
    <NotificationContext.Provider
      value={{
        addNotification,
        // socket / push
        socketRef,
        notifications,
        setNotifications,
        liveUpdatesEnabled,
        setLiveUpdatesEnabled,

        // public jackpot counters
        jackpot,
        setJackpot,
        totalTickets,
        setTotalTickets,
        participants,
        setParticipants,

        notificationCounters,
        clearNotificationCount,

        // winner / tickets & claim helpers
        winnerTicket,
        globalTickets,
        claimGlobalJackpot: handleClaim,
        claiming,
        jackpotClaimed,
        seasonId,
        setSeasonId,
      }}
    >
      {children}

      {showWinnerPopup && (
        <GlobalJackpotWinnerPopup
          winningTicket={winnerTicket}
          isWinner={globalTickets.includes(winnerTicket)}
          onClaim={handleClaim}
          onClose={() => setShowWinnerPopup(false)}
          claiming={claiming}
        />
      )}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
